// Import types but use 'type' keyword to avoid unused import warnings
import type { ProcessingResult, ValidationSummary } from '../types/reportTypes';
import { buildApiUrl, ENDPOINTS, API_BASE_URL, shouldUseMockData, mockApiResponse } from '../config/api.config';

// Token refresh configuration
const TOKEN_REFRESH_BUFFER = 60000; // 1 minute buffer before expiration
const MAX_RETRY_ATTEMPTS = 2;

// Validate API_BASE_URL configuration on startup
(function validateApiBaseUrl() {
  if (!API_BASE_URL) {
    console.error('API_BASE_URL is not configured!');
  } else if (!API_BASE_URL.includes('/api')) {
    console.error(`API_BASE_URL "${API_BASE_URL}" is missing the required /api suffix!`);
    console.error('Authentication and other API requests will fail!');
  } else {
    console.log(`API configured with base URL: ${API_BASE_URL}`);
  }
})();

/**
 * Get the proper base URL for auth endpoints
 * Auth endpoints are at /api/auth/... not /auth/...
 */
function getAuthUrl(endpoint: string): string {
  // With proxy setup, we can use simple relative URLs
  // This will be proxied to the backend correctly
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;

  // Handle potential 'auth/' prefix in the endpoint
  const authEndpoint = cleanEndpoint.startsWith('auth/')
    ? cleanEndpoint.substring(5) // Remove the 'auth/' part
    : cleanEndpoint;

  // Simple construction for proxy - all requests start with /api 
  const authUrl = `/api/auth/${authEndpoint}`;
  
  console.log(`Auth URL constructed: ${authUrl}`);
  return authUrl;
}

/**
 * Service for interacting with the ROI Marketing API
 */
class ApiService {
  // Flag to prevent multiple refresh attempts at the same time
  private isRefreshing = false;
  // Track pending requests to retry after refresh
  private pendingRequests: Array<() => void> = [];

  // Check if token is expired or about to expire
  private isTokenExpiredOrExpiring(): boolean {
    const token = localStorage.getItem('authToken');
    if (!token) return true;
    
    // Check if this is a Netlify deployment with a mock token
    const isNetlifyDeployment = 
      window.location.hostname.includes('netlify.app') || 
      window.location.hostname.includes('github.io') ||
      window.location.hostname.includes('reporting.roiconstruction.com') ||
      process.env.REACT_APP_USE_MOCK_DATA === 'true' ||
      process.env.REACT_APP_NETLIFY === 'true';
    
    // Never expire Netlify mock tokens
    if (isNetlifyDeployment && token === 'mock-auth-token-for-netlify') {
      return false;
    }

    // Check the last refresh time - if we refreshed recently, skip additional checks
    const lastRefreshTime = localStorage.getItem('lastTokenRefreshTime');
    if (lastRefreshTime) {
      const timeSinceRefresh = Date.now() - parseInt(lastRefreshTime, 10);

      // If we refreshed less than 10 minutes ago, don't refresh again regardless of expiration
      if (timeSinceRefresh < 10 * 60 * 1000) {
        console.log('Token was refreshed recently, skipping expiration check');
        return false;
      }
    }

    try {
      // For JWT format tokens, check expiration
      if (token.split('.').length === 3) {
        // Extract expiration from token payload
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (!payload.exp) return false;

        // Check if token is expired or about to expire (with buffer time)
        const expirationTime = payload.exp * 1000; // Convert to milliseconds
        const shouldRefresh = Date.now() + TOKEN_REFRESH_BUFFER >= expirationTime;

        if (shouldRefresh) {
          console.log(`Token expiring soon (in ${Math.round((expirationTime - Date.now()) / 1000)} seconds)`);
        }

        return shouldRefresh;
      }
      
      // For non-JWT tokens (like mock tokens), don't attempt refresh
      return false;
    } catch (error) {
      console.error('Error parsing token:', error);
      return false; // Changed to false to avoid constant refresh attempts on error
    }
  }
  
  // Get authentication headers from localStorage
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      Authorization: token ? `Bearer ${token}` : '',
    };
  }
  
  /**
   * Perform a request with automatic token refresh
   * @param url The URL to fetch
   * @param options Request options
   * @param errorContext Context for error messages
   * @returns Response object
   */
  async fetchWithTokenRefresh(url: string, options: RequestInit, errorContext: string): Promise<Response> {
    // Check if this is a Netlify deployment
    const isNetlifyDeployment = 
      window.location.hostname.includes('netlify.app') || 
      window.location.hostname.includes('github.io') ||
      window.location.hostname.includes('reporting.roiconstruction.com') ||
      process.env.REACT_APP_USE_MOCK_DATA === 'true' ||
      process.env.REACT_APP_NETLIFY === 'true';
    
    if (isNetlifyDeployment) {
      // For Netlify deployments, we'll mock API responses instead of making real network requests
      console.log(`Mock API call to ${url} for Netlify deployment`);
      
      // Create a mock response based on the URL
      if (url.includes('/reports')) {
        return Promise.resolve(new Response(JSON.stringify({
          data: [],
          success: true,
          message: "Mock reports API response for Netlify deployment"
        }), {
          status: 200,
          headers: { 'Content-Type': 'application/json' }
        }));
      }
      
      // Default mock response
      return Promise.resolve(new Response(JSON.stringify({
        success: true,
        message: "Mock API response for Netlify deployment"
      }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' }
      }));
    }
    
    // Normal token refresh logic for non-Netlify deployments
    if (this.isTokenExpiredOrExpiring() && !url.includes('auth/refresh')) {
      // Use a semaphore pattern to prevent multiple refresh attempts
      if (!this.isRefreshing) {
        this.isRefreshing = true;
        console.log('Refreshing auth token...');
        
        try {
          const refreshToken = localStorage.getItem('refreshToken');
          if (!refreshToken) {
            throw new Error('No refresh token available');
          }
          
          // Use the proper URL construction method
          const refreshUrl = getAuthUrl('refresh');

          console.log('Using getAuthUrl for refresh URL (in fetchWithTokenRefresh):', refreshUrl);
          console.log('API_BASE_URL value:', API_BASE_URL);

          const response = await fetch(refreshUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken }),
          });
          
          if (response.ok) {
            const data = await response.json();
            
            // Support both data wrapper format and direct format
            const responseData = data.data || data;
            
            const token = responseData.access_token || responseData.accessToken;
            
            localStorage.setItem('authToken', token);

            // Store refresh timestamp to prevent excessive refreshing
            localStorage.setItem('lastTokenRefreshTime', Date.now().toString());

            if (responseData.refresh_token || responseData.refreshToken) {
              localStorage.setItem('refreshToken',
                responseData.refresh_token || responseData.refreshToken);
            }
            
            // Add the new token to the request
            if (options.headers) {
              options.headers = {
                ...options.headers,
                Authorization: `Bearer ${token}`,
              };
            }
            
            // Process any pending requests
            this.pendingRequests.forEach(callback => callback());
            this.pendingRequests = [];
          } else {
            throw new Error(`Refresh failed: ${response.status}`);
          }
        } catch (error) {
          console.error('Token refresh failed:', error);
          // Clear tokens on failed refresh
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
        } finally {
          this.isRefreshing = false;
        }
      } else {
        // Another request is already refreshing the token,
        // wait for it to complete and then retry
        return new Promise<Response>((resolve) => {
          this.pendingRequests.push(() => {
            // Update the Authorization header with the new token
            const token = localStorage.getItem('authToken');
            if (token && options.headers) {
              options.headers = {
                ...options.headers,
                Authorization: `Bearer ${token}`,
              };
            }
            resolve(fetch(url, options));
          });
        });
      }
    }
    
    // Make the request with retry logic
    let retryCount = 0;
    let lastError: Error | null = null;
    
    while (retryCount <= MAX_RETRY_ATTEMPTS) {
      try {
        // Always include credentials for CSRF cookie support
        const optionsWithCredentials = {
          ...options,
          credentials: 'include' as RequestCredentials,
        };

        const response = await fetch(url, optionsWithCredentials);

        // If unauthorized and we haven't refreshed the token yet,
        // refresh and retry one more time
        if (response.status === 401 &&
            !url.includes('auth/refresh') &&
            !this.isRefreshing &&
            retryCount === 0) {
          // Force token refresh and retry
          localStorage.removeItem('authToken');
          return this.fetchWithTokenRefresh(url, options, errorContext);
        }

        return response;
      } catch (error) {
        lastError = error as Error;
        retryCount++;
        
        if (retryCount <= MAX_RETRY_ATTEMPTS) {
          console.log(`Retry ${retryCount}/${MAX_RETRY_ATTEMPTS} for ${url}`);
          // Exponential backoff: 500ms, 1500ms, 4500ms
          const delay = 500 * Math.pow(3, retryCount - 1);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    throw lastError || new Error(`Failed to fetch ${url}`);
  }
  
  /**
   * Handle API request errors with improved logging
   */
  async handleResponse(response: Response, context: string) {
    if (!response.ok) {
      console.log(`${context} response status:`, response.status, response.statusText);
      console.log('URL:', response.url);
      
      try {
        const contentType = response.headers.get('Content-Type');
        
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          console.error(`${context} error:`, response.status, '-', errorData.message || response.statusText);
          throw new Error(errorData.message || `API error: ${response.status}`);
        } else {
          try {
            const errorText = await response.text();
            console.error(`${context} error:`, response.status, '-', response.statusText);
            console.error('Response:', errorText);
            throw new Error(`API error: ${response.status}`);
          } catch (e) {
            console.error('Failed to parse error response:', e);
            throw new Error(`API error: ${response.status} - ${response.statusText}`);
          }
        }
      } catch (e) {
        console.error(`${context} error:`, response.status, '-', response.statusText);
        throw new Error(`API error: ${response.status}`);
      }
    }
    return response;
  }
  
  /**
   * Login with email and password
   * @param email User email
   * @param password User password
   * @returns Auth response with token and user data
   */
  async login(email: string, password: string) {
    // For static deployments, use mock data
    if (shouldUseMockData()) {
      console.log('Using mock login data for static deployment');
      return mockApiResponse({
        accessToken: 'mock-token-for-static-deployment',
        refreshToken: 'mock-refresh-token',
        user: {
          id: '1',
          firstName: 'Demo',
          lastName: 'User',
          email: 'demo@roiexteriors.com',
          role: 'admin'
        }
      });
    }
    
    console.log('Logging in with email:', email);

    try {
      // Get CSRF token first (for Laravel's CSRF protection)
      // API_BASE_URL already includes the /api prefix - we need to construct the sanctum URL base
      const baseUrl = API_BASE_URL.substring(0, API_BASE_URL.lastIndexOf('/api'));
      const csrfUrl = `${baseUrl}/sanctum/csrf-cookie`;
      console.log('Getting CSRF token from:', csrfUrl);

      try {
        const csrfResponse = await fetch(csrfUrl, {
          method: 'GET',
          credentials: 'include', // Include cookies
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (csrfResponse.ok) {
          // Check if the response has content
          const text = await csrfResponse.text();

          if (text && text.length > 0) {
            try {
              const csrfData = JSON.parse(text);
              console.log('CSRF token received:', csrfData.csrf_token ? 'Yes' : 'No');
            } catch (parseError) {
              console.log('CSRF token received (empty or non-JSON response)');
            }
          } else {
            // Empty response is OK - Laravel Sanctum sets the cookie without a response body
            console.log('CSRF cookie set (empty response)');
          }
        } else {
          console.warn('Failed to get CSRF token, proceeding anyway:', csrfResponse.status);
        }
      } catch (csrfError) {
        console.warn('Error getting CSRF token, proceeding anyway:', csrfError);
        console.debug('CSRF error details:', csrfError);
      }

      // Use the proper URL construction method
      const loginUrl = getAuthUrl('login');

      console.log('Using getAuthUrl for login URL:', loginUrl);
      console.log('API_BASE_URL value:', API_BASE_URL);

      // Additional logging to help debug the issue
      console.log('getAuthUrl(login) returned:', getAuthUrl('login'));
      console.log('API_BASE_URL + /auth/login:', `${API_BASE_URL}/auth/login`);

      try {
        const response = await fetch(loginUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ email, password }),
          credentials: 'include', // Include cookies
        });

        // Detailed logging for debugging
        console.log(`Login response status: ${response.status} ${response.statusText}`);
        console.log('Login response headers:', Object.fromEntries(response.headers.entries()));

        const data = await this.handleResponse(response, 'Login').then(r => r.json());
        console.log('Login successful, parsing response data');

        // Extract data from response
        if (data && data.data) {
          // Handle response wrapper with success: true, data: {...}
          console.log('Response has data wrapper structure');
          const responseData = data.data;

          // Save token and user data to localStorage
          // Handle both accessToken and access_token formats for compatibility
          const token = responseData.access_token || responseData.accessToken;
          const refreshToken = responseData.refresh_token || responseData.refreshToken;

          if (token) {
            console.log('Token found in response, storing in localStorage');
            localStorage.setItem('authToken', token);

            // Store refresh timestamp to prevent excessive refreshing
            localStorage.setItem('lastTokenRefreshTime', Date.now().toString());

            // Also set last verification time since login counts as verification
            localStorage.setItem('lastVerificationTime', Date.now().toString());

            // Store refresh token if provided
            if (refreshToken) {
              console.log('Refresh token found in response, storing in localStorage');
              localStorage.setItem('refreshToken', refreshToken);
            }

            // Validate there's a user object
            if (!responseData.user) {
              console.warn('No user data in response:', responseData);
            }

            return responseData;
          } else {
            console.error('No token found in response:', data);
            throw new Error('Invalid response format: no token found in data wrapper');
          }
        } else {
          // Direct response format
          console.log('Response has direct data structure');

          // Save token and user data to localStorage
          // Handle both accessToken and access_token formats for compatibility
          const token = data.access_token || data.accessToken;
          const refreshToken = data.refresh_token || data.refreshToken;

          if (token) {
            console.log('Token found in response, storing in localStorage');
            localStorage.setItem('authToken', token);

            // Store refresh timestamp to prevent excessive refreshing
            localStorage.setItem('lastTokenRefreshTime', Date.now().toString());

            // Also set last verification time since login counts as verification
            localStorage.setItem('lastVerificationTime', Date.now().toString());

            // Store refresh token if provided
            if (refreshToken) {
              console.log('Refresh token found in response, storing in localStorage');
              localStorage.setItem('refreshToken', refreshToken);
            }

            // Validate there's a user object
            if (!data.user) {
              console.warn('No user data in response:', data);
            }

            return data;
          } else {
            console.error('No token found in response:', data);
            throw new Error('Invalid response format: no token found in direct response');
          }
        }
      } catch (fetchError: any) {
        // Enhanced error logging for network issues
        console.error('Fetch error during login:', fetchError);

        if (fetchError.name === 'TypeError' && fetchError.message.includes('Failed to fetch')) {
          console.error('Network error - backend server may be unavailable');
          throw new Error('Unable to connect to authentication server. Please verify the backend is running at http://localhost:3001.');
        } else if (fetchError.status === 401) {
          throw new Error('Invalid email or password. Please check your credentials and try again.');
        } else if (fetchError.status === 403) {
          throw new Error('Access forbidden. Your account may not have permission to log in.');
        } else if (fetchError.status === 429) {
          throw new Error('Too many login attempts. Please wait a moment and try again.');
        } else if (fetchError.status >= 500) {
          throw new Error('Server error. The authentication service is experiencing difficulties.');
        }

        // Re-throw with more context if none of the specific cases match
        throw new Error(`Login failed: ${fetchError.message || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Login process error:', error);
      throw error;
    }
  }
  
  /**
   * Register a new user
   * @param firstName User first name
   * @param lastName User last name
   * @param email User email
   * @param password User password
   * @returns Auth response with token and user data
   */
  async register(firstName: string, lastName: string, email: string, password: string) {
    // Use the proper URL construction method
    const registerUrl = getAuthUrl('register');

    console.log('Using getAuthUrl for register URL:', registerUrl);
    console.log('API_BASE_URL value:', API_BASE_URL);
    
    const response = await fetch(registerUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ firstName, lastName, email, password }),
    });
    
    const data = await this.handleResponse(response, 'Register').then(r => r.json());
    
    // Save token and user data to localStorage
    const token = data.accessToken || data.access_token;
    const refreshToken = data.refreshToken || data.refresh_token;
    
    localStorage.setItem('authToken', token);
    
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
    
    return data;
  }
  
  /**
   * Verify authentication token
   * @returns User profile data if token is valid
   */
  async verifyAuth() {
    // For static deployments, use mock data
    if (shouldUseMockData()) {
      console.log('Using mock user data for static deployment');
      const mockUser = {
        id: '1',
        firstName: 'Demo',
        lastName: 'User',
        email: 'demo@roiexteriors.com',
        role: 'admin'
      };
      
      // Store mock data in localStorage for consistency
      localStorage.setItem('user', JSON.stringify(mockUser));
      localStorage.setItem('lastVerificationTime', Date.now().toString());
      
      return mockApiResponse(mockUser);
    }
    
    // Check if we've verified recently and already have user data
    const lastVerificationTime = localStorage.getItem('lastVerificationTime');
    const cachedUser = localStorage.getItem('user');

    if (lastVerificationTime && cachedUser) {
      const timeSinceVerification = Date.now() - parseInt(lastVerificationTime, 10);

      // If we verified less than 10 minutes ago and have user data,
      // return the cached user data instead of making an API call
      if (timeSinceVerification < 10 * 60 * 1000) {
        console.log('Using cached user data instead of verifying with backend');
        try {
          return JSON.parse(cachedUser);
        } catch (e) {
          console.warn('Failed to parse cached user data, proceeding with verification');
        }
      }
    }

    console.log('Verifying authentication with backend...');

    // Try the GET /profile endpoint first (which we added specifically for this purpose)
    try {
      // Use the proper URL construction method
      const profileUrl = getAuthUrl('profile');

      console.log('Using getAuthUrl for profile URL:', profileUrl);
      console.log('API_BASE_URL value:', API_BASE_URL);

      const response = await this.fetchWithTokenRefresh(
        profileUrl,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
          credentials: 'include', // Include cookies
        },
        'verifyAuth'
      );

      // Update lastVerificationTime in localStorage
      localStorage.setItem('lastVerificationTime', Date.now().toString());

      const data = await this.handleResponse(response, 'verifyAuth').then(r => r.json());
      
      // Handle wrapped response format from BaseController
      if (data && data.data) {
        console.log('Profile response has data wrapper structure');
        return data.data;
      }
      
      return data;
    } catch (error) {
      // If GET /profile fails, try the /user endpoint as fallback
      console.warn('GET /profile failed, trying /user endpoint as fallback:', error);

      // Use the proper URL construction method
      const userUrl = getAuthUrl('user');

      console.log('Using getAuthUrl for user URL:', userUrl);
      console.log('API_BASE_URL value:', API_BASE_URL);

      const response = await this.fetchWithTokenRefresh(
        userUrl,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
          credentials: 'include', // Include cookies
        },
        'verifyAuth'
      );

      // Update lastVerificationTime in localStorage
      localStorage.setItem('lastVerificationTime', Date.now().toString());

      const data = await this.handleResponse(response, 'verifyAuth').then(r => r.json());
      
      // Handle wrapped response format from BaseController
      if (data && data.data) {
        console.log('Profile response has data wrapper structure');
        return data.data;
      }
      
      return data;
    }
  }
  
  /**
   * Refresh the authentication token manually
   * @returns New token data if successful
   */
  async refreshAuthToken() {
    const refreshToken = localStorage.getItem('refreshToken');

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    // Use the proper URL construction method
    const refreshUrl = getAuthUrl('refresh');

    console.log('Using getAuthUrl for refresh URL:', refreshUrl);
    console.log('API_BASE_URL value:', API_BASE_URL);

    try {
      const response = await fetch(refreshUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'include', // Include cookies for CSRF
        body: JSON.stringify({ refresh_token: refreshToken }), 
      });

      if (!response.ok) {
        console.error('Token refresh failed with status:', response.status);
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Token refresh failed with status: ${response.status}`);
      }

      const data = await response.json();

      // Extract data from wrapped response if necessary
      const responseData = data.data || data;

      // Update tokens in localStorage
      const token = responseData.access_token || responseData.accessToken;
      const newRefreshToken = responseData.refresh_token || responseData.refreshToken;

      if (!token) {
        console.error('No token in refresh response:', responseData);
        throw new Error('Invalid token refresh response: No token received');
      }

      console.log('Token refreshed successfully');
      localStorage.setItem('authToken', token);

      // Store refresh timestamp to prevent excessive refreshing
      localStorage.setItem('lastTokenRefreshTime', Date.now().toString());

      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken);
      }

      return responseData;
    } catch (error) {
      console.error('Token refresh error:', error);
      throw error;
    }
  }
  
  /**
   * Logout user and remove stored tokens
   * @returns Logout status
   */
  async logout() {
    // Optionally call backend to invalidate the token
    try {
      // Use the proper URL construction method
      const logoutUrl = getAuthUrl('logout');

      console.log('Using getAuthUrl for logout URL:', logoutUrl);
      console.log('API_BASE_URL value:', API_BASE_URL);
      
      const response = await this.fetchWithTokenRefresh(
        logoutUrl,
        {
          method: 'POST',
          headers: this.getAuthHeaders(),
        },
        'logout'
      );
      
      await this.handleResponse(response, 'Logout');
      console.log('Logout successful');
    } catch (err) {
      console.error('Error during logout:', err);
      // Continue with local logout even if API call fails
    }
    
    // Clear stored tokens
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    
    return { success: true };
  }
  
  /**
   * Get list of reports
   * @returns List of report data
   */
  async getReports() {
    const response = await this.fetchWithTokenRefresh(
      buildApiUrl(ENDPOINTS.REPORTS.LIST),
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      },
      'getReports'
    );
    
    return this.handleResponse(response, 'GetReports').then(r => r.json());
  }
  
  /**
   * Get detailed report data
   * @param id Report ID
   * @returns Complete report data
   */
  async getReport(id: string) {
    const response = await this.fetchWithTokenRefresh(
      buildApiUrl(ENDPOINTS.REPORTS.GET(id)),
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      },
      'getReport'
    );
    
    return this.handleResponse(response, 'GetReport').then(r => r.json());
  }
  
  /**
   * Delete a report
   * @param id Report ID
   * @returns Deletion status
   */
  async deleteReport(id: string) {
    const response = await this.fetchWithTokenRefresh(
      buildApiUrl(ENDPOINTS.REPORTS.DELETE(id)),
      {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      },
      'deleteReport'
    );
    
    return this.handleResponse(response, 'DeleteReport').then(r => r.json());
  }
  
  /**
   * Generate a new report
   * @param title Report title
   * @param period Report period information
   * @param salesDataImportIds Sales data import IDs
   * @param adSpendDataImportIds Ad spend data import IDs
   * @returns Generated report ID and summary
   */
  async generateReport(
    title: string,
    period: { month: number; year: number } = { month: new Date().getMonth() + 1, year: new Date().getFullYear() },
    salesDataImportIds: string[] = [],
    adSpendDataImportIds: string[] = []
  ) {
    const params = {
      title,
      period,
      salesDataImportIds,
      adSpendDataImportIds
    };

    const response = await this.fetchWithTokenRefresh(
      buildApiUrl(ENDPOINTS.REPORTS.GENERATE),
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(params),
      },
      'generateReport'
    );

    return this.handleResponse(response, 'GenerateReport').then(r => r.json());
  }

  /**
   * Generate a new report with simplified parameters
   * @param params Report generation parameters
   * @returns Generated report ID and summary
   */
  async generateReportWithParams(params: any) {
    const response = await this.fetchWithTokenRefresh(
      buildApiUrl(ENDPOINTS.REPORTS.GENERATE),
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(params),
      },
      'generateReport'
    );

    return this.handleResponse(response, 'GenerateReport').then(r => r.json());
  }
  
  /**
   * Export report in specified format
   * @param id Report ID
   * @param format Export format (pdf, csv, xlsx)
   * @returns URL to download the exported file
   */
  async exportReport(id: string, format: string) {
    const response = await this.fetchWithTokenRefresh(
      buildApiUrl(ENDPOINTS.REPORTS.EXPORT(id, format)),
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      },
      'exportReport'
    );
    
    return this.handleResponse(response, 'ExportReport').then(r => r.json());
  }
  
  /**
   * Create a new import session
   * @param importType Type of import (sales_data, ad_spend_data)
   * @param originalFilename Original filename
   * @returns Import session details
   */
  async createImportSession(importType: string, originalFilename: string) {
    const response = await this.fetchWithTokenRefresh(
      buildApiUrl(ENDPOINTS.IMPORTS.SESSIONS),
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          importType,
          version: '1.0',
          originalFilename
        }),
      },
      'createImportSession'
    );

    return this.handleResponse(response, 'CreateImportSession').then(r => r.json());
  }

  /**
   * Upload a CSV file for processing
   * @param file File to upload
   * @param type File type (sales or adspend)
   * @returns Import session details
   */
  async uploadCSV(file: File, type: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    const headers = this.getAuthHeaders();
    // Remove Content-Type for FormData
    delete (headers as any)['Content-Type'];

    const response = await this.fetchWithTokenRefresh(
      buildApiUrl(type === 'sales_data' ? ENDPOINTS.IMPORTS.SALES : ENDPOINTS.IMPORTS.ADSPEND),
      {
        method: 'POST',
        headers,
        body: formData,
      },
      'uploadCSV'
    );

    return this.handleResponse(response, 'UploadCSV').then(r => r.json());
  }
  
  /**
   * Process an imported CSV file
   * @param importId Import session ID
   * @param mappings Column mappings
   * @returns Processing result
   */
  async processCSV(importId: string, mappings: any): Promise<ProcessingResult> {
    const response = await this.fetchWithTokenRefresh(
      buildApiUrl(ENDPOINTS.IMPORTS.PROCESS(importId)),
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ mappings }),
      },
      'processCSV'
    );
    
    return this.handleResponse(response, 'ProcessCSV').then(r => r.json());
  }
  
  /**
   * Validate an import mapping before processing
   * @param importId Import session ID
   * @param mappings Column mappings
   * @returns Validation summary
   */
  async validateMapping(importId: string, mappings: any): Promise<ValidationSummary> {
    const response = await this.fetchWithTokenRefresh(
      buildApiUrl(ENDPOINTS.IMPORTS.VALIDATE(importId)),
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ mappings }),
      },
      'validateMapping'
    );
    
    return this.handleResponse(response, 'ValidateMapping').then(r => r.json());
  }
}

// Create and export singleton instance
const apiService = new ApiService();
export { apiService };